import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Uploader from "@/components/Uploader";
import MediaView from "@/components/MediaView";
import { Heart, Plus, Trash2, X, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

export default function Memories() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [takenOn, setTakenOn] = useState("");
  const [upload, setUpload] = useState(null); // { path, kind }
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    const { data } = await api.get("/memories");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!upload?.path) {
      toast.error("Please upload a photo or video first");
      return;
    }
    setBusy(true);
    try {
      await api.post("/memories", {
        caption,
        media_path: upload.path,
        media_kind: upload.kind || "image",
        taken_on: takenOn || null,
      });
      setCaption(""); setTakenOn(""); setUpload(null); setOpen(false);
      load();
      toast.success("Memory added to the wall");
    } catch {
      toast.error("Could not save memory");
    } finally { setBusy(false); }
  };

  const heart = async (m) => {
    const { data } = await api.post(`/memories/${m.id}/heart`);
    setItems(items.map((x) => x.id === m.id ? data : x));
  };

  const remove = async (m) => {
    if (!window.confirm("Remove this memory?")) return;
    await api.delete(`/memories/${m.id}`);
    setItems(items.filter((x) => x.id !== m.id));
    setLightbox(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-burgundy">Memory Wall</div>
          <h1 className="font-serif text-4xl text-jjblue mt-2">Holy moments, kept forever</h1>
          <p className="mt-2 text-[var(--jj-text-secondary)] max-w-2xl">
            Upload photos and videos of the things He's done. Baptisms, gatherings, answered prayers — a quiet gallery for the community to remember together.
          </p>
        </div>
        <button onClick={() => setOpen(!open)} className="btn-primary flex items-center gap-2" data-testid="new-memory-btn">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add memory
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="jj-card p-6 mt-6 max-w-3xl" data-testid="new-memory-form">
          <div className="flex items-center justify-between mb-4">
            <Uploader accept="image/*,video/*"
              label={upload ? `${upload.kind} attached — replace` : "Choose a photo or video"}
              onUploaded={(d) => setUpload(d)} testId="m-upload" />
            {upload && <button type="button" onClick={() => setUpload(null)} className="text-xs text-[var(--jj-muted)] underline">remove</button>}
          </div>
          {upload && (
            <div className="mb-4 rounded-xl overflow-hidden border border-jj max-h-72">
              <MediaView path={upload.path} kind={upload.kind} className="w-full max-h-72 object-cover" />
            </div>
          )}
          <label className="text-sm text-jjblue/80">Caption</label>
          <textarea rows={2} className="input-jj mt-1" placeholder="A line about this moment…"
            value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="m-caption" />
          <div className="mt-3 flex items-center gap-3">
            <label className="text-sm text-jjblue/80">When?</label>
            <input type="date" className="input-jj max-w-[200px]" value={takenOn} onChange={(e) => setTakenOn(e.target.value)} data-testid="m-date" />
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="m-submit">{busy ? "Saving…" : "Add to wall"}</button>
          </div>
        </form>
      )}

      {items.length === 0 && (
        <div className="text-[var(--jj-muted)] text-center py-16">
          No memories yet — be the first to upload one.
        </div>
      )}

      <div className="mt-8 columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 space-y-5">
        {items.map((m) => {
          const canDel = m.author?.id === user?.id || user?.role === "admin";
          return (
          <div key={m.id} className="break-inside-avoid jj-card overflow-hidden group relative fade-up" data-testid={`memory-${m.id}`}>
            <button type="button" onClick={() => setLightbox(m)} className="block w-full text-left">
              <MediaView path={m.media_path} kind={m.media_kind}
                className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
            </button>
            {canDel && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(m); }}
                className="absolute top-3 right-3 p-1.5 rounded-full bg-jjblue/85 text-cream opacity-0 group-hover:opacity-100 transition-opacity hover:bg-burgundy"
                title="Delete memory"
                aria-label="Delete memory"
                data-testid={`memory-tile-delete-${m.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.7} />
              </button>
            )}
            <div className="p-4">
              {m.caption && <p className="text-sm text-jjblue/90 leading-relaxed">{m.caption}</p>}
              <div className="mt-3 flex items-center justify-between text-xs text-[var(--jj-muted)]">
                <span>{m.author?.name}{m.taken_on ? ` · ${new Date(m.taken_on).toLocaleDateString()}` : ""}</span>
                <button onClick={() => heart(m)} className={`inline-flex items-center gap-1 ${m.i_hearted ? "text-burgundy" : "hover:text-burgundy"} transition-colors`} data-testid={`heart-${m.id}`}>
                  <Heart className={`h-4 w-4 ${m.i_hearted ? "fill-current" : ""}`} strokeWidth={1.6} /> {m.hearts}
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-[#1A2B4C]/85 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between text-cream mb-3">
              <div className="text-sm">{lightbox.author?.name}{lightbox.taken_on ? ` · ${new Date(lightbox.taken_on).toLocaleDateString()}` : ""}</div>
              <div className="flex items-center gap-2">
                {(lightbox.author?.id === user?.id || user?.role === "admin") && (
                  <button onClick={() => remove(lightbox)} className="text-cream/80 hover:text-cream p-2" data-testid="memory-delete"><Trash2 className="h-5 w-5" /></button>
                )}
                <button onClick={() => setLightbox(null)} className="text-cream/80 hover:text-cream p-2" data-testid="memory-close"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden bg-black">
              <MediaView path={lightbox.media_path} kind={lightbox.media_kind} className="w-full max-h-[78vh] object-contain mx-auto" />
            </div>
            {lightbox.caption && <p className="mt-4 text-cream/90 text-center max-w-2xl mx-auto">{lightbox.caption}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
